import './styles.css'

import { useCallback, useEffect, useMemo } from 'react'

import { Box, HStack, Popover, Portal, StackSeparator, useDisclosure } from '@chakra-ui/react'
import {
  CubeBackIcon,
  CubeDownIcon,
  CubeFrontIcon,
  CubeLeftIcon,
  CubeRightIcon,
  CubeUpIcon,
} from '@editor/assets/icons'
import { EDITOR_TOOL_BUTTON_SIZE } from '@editor/config/constants'
import { CameraViewOrientation } from '@editor/interfaces/camera'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useConfigureCanvas from '@editor/pages/projects/blueprint-editor/hooks/useConfigureCanvas'
import { setSelectedTool } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import CameraFocusButton from '../CameraFocusButton'
import CameraViewButton from '../CameraViewButton'

/**
 * Timeout for the camera profile sub-toolbar to close.
 * Value is in milliseconds.
 */
const CAMERA_PROFILE_SUB_TOOLBAR_TIMEOUT = 3 * 1000

/**
 * Camera sub-toolbar timeout.
 */
let subToolbarTimeout: NodeJS.Timeout

export const CameraToolbar = () => {
  // Context
  const editorContext = useBlueprintEditorContext()

  // Popover toolbar
  const { open, onOpen, onClose } = useDisclosure()
  const { orientCamera } = useConfigureCanvas(editorContext)

  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.BlueprintManager, [selectedTool])
  const canOtherPerspective = mode === Mode.Editor3D

  /**
   * Re-orient camera to saved profile.
   * @param orientation Camera view orientation.
   * @param noAutoClose Flag to not auto close the camera profile sub-toolbar.
   */
  const orientCameraToProfile = useCallback(
    (orientation: CameraViewOrientation, noAutoClose?: boolean) => {
      orientCamera(mode, orientation)
      if (!noAutoClose) onClose()
    },
    [orientCamera, onClose, mode],
  )

  /**
   * Clicking the main view button will orient the camera to the front view
   * as well as open the camera profile sub-toolbar.
   */
  const onClickMainView = useCallback(() => {
    orientCameraToProfile(CameraViewOrientation.FRONT, true)

    if (!canOtherPerspective) return

    onOpen()
    clearTimeout(subToolbarTimeout)
    subToolbarTimeout = setTimeout(() => {
      onClose()
    }, CAMERA_PROFILE_SUB_TOOLBAR_TIMEOUT)
  }, [canOtherPerspective, onOpen, onClose, orientCameraToProfile])

  /**
   * Close sub-toolbar when the selected tool is camera profile.
   */
  useEffect(() => {
    if (isToolSelected) {
      onClose()
    }
  }, [isToolSelected, onClose])

  return (
    <HStack
      id="camera-toolbar"
      h={EDITOR_TOOL_BUTTON_SIZE}
      position="absolute"
      bottom={2}
      left={2}
      zIndex={20}
      backgroundColor="gray2.800"
      borderRadius="md"
      separator={<StackSeparator borderColor="whiteAlpha.200" />}
      gap={0}
      data-testid="camera-assistant-bar"
    >
      {mode === Mode.Editor3D && (
        <CameraFocusButton
          selectedTool={selectedTool}
          changeTool={(newTool) => dispatch(setSelectedTool(newTool as unknown as BlueprintEditorTools))}
        />
      )}

      {mode === Mode.Editor2D && selectedBlueprint && !isCreatingBlueprint ? (
        <Box w={EDITOR_TOOL_BUTTON_SIZE} h={EDITOR_TOOL_BUTTON_SIZE} position="relative">
          <CameraViewButton
            label="カメラを正面(-y)からの視点に移動"
            icon={<CubeFrontIcon width="50%" height="50%" data-testid="cameraFromFront" />}
            onClick={onClickMainView}
            onLongPress={canOtherPerspective ? onOpen : undefined}
            hasSubtoolbar={canOtherPerspective}
          />
          {canOtherPerspective && (
            <Box position="relative" bottom="calc(100% + 4px)">
              <Popover.Root
                open={open}
                onOpenChange={(e) => (e.open ? onOpen() : onClose())}
                closeOnInteractOutside
                closeOnEscape
              >
                <Portal>
                  <Popover.Positioner>
                    <Popover.Content
                      h={EDITOR_TOOL_BUTTON_SIZE}
                      w="auto"
                      borderWidth={0}
                      backgroundColor="transparent"
                      bottom={EDITOR_TOOL_BUTTON_SIZE}
                      marginBottom={1}
                    >
                      <Popover.Arrow
                        w="8px"
                        h="8px"
                        top={EDITOR_TOOL_BUTTON_SIZE}
                        left="12px"
                        transform="rotate(45deg) translate(0, -5px)"
                        backgroundColor="gray2.800"
                        boxShadow="1px 1px 1px 0 {colors.whiteAlpha.500}"
                        position="absolute"
                      />
                      <Popover.Body
                        p={0}
                        backgroundColor="secondary.800"
                        borderColor="secondary.700"
                        borderWidth={1}
                        borderRadius="md"
                      >
                        <HStack
                          h={EDITOR_TOOL_BUTTON_SIZE}
                          backgroundColor="gray2.800"
                          borderRadius="md"
                          separator={<StackSeparator borderColor="whiteAlpha.200" />}
                          gap={0}
                          data-testid="camera-profile-view-orientation-bar"
                        >
                          <CameraViewButton
                            label="カメラを左(-x)からの視点に移動"
                            icon={<CubeLeftIcon width="50%" height="50%" data-testid="cameraFromLeft" />}
                            onClick={() => orientCameraToProfile(CameraViewOrientation.LEFT)}
                          />
                          <CameraViewButton
                            label="カメラを右(+x)からの視点に移動"
                            icon={<CubeRightIcon width="50%" height="50%" data-testid="cameraFromRight" />}
                            onClick={() => orientCameraToProfile(CameraViewOrientation.RIGHT)}
                          />
                          <CameraViewButton
                            label="カメラを正面(-y)からの視点に移動"
                            icon={<CubeFrontIcon width="50%" height="50%" data-testid="cameraFromFront" />}
                            onClick={() => orientCameraToProfile(CameraViewOrientation.FRONT)}
                          />
                          <CameraViewButton
                            label="カメラを裏(+y)からの視点に移動"
                            icon={<CubeBackIcon width="50%" height="50%" data-testid="cameraFromBack" />}
                            onClick={() => orientCameraToProfile(CameraViewOrientation.BACK)}
                          />
                          <CameraViewButton
                            label="カメラを下(-z)からの視点に移動"
                            icon={<CubeDownIcon width="50%" height="50%" data-testid="cameraFromBottom" />}
                            onClick={() => orientCameraToProfile(CameraViewOrientation.BOTTOM)}
                          />
                          <CameraViewButton
                            label="カメラを上(+z)からの視点に移動"
                            icon={<CubeUpIcon width="50%" height="50%" data-testid="cameraFromTop" />}
                            onClick={() => orientCameraToProfile(CameraViewOrientation.TOP)}
                          />
                          {/* {isAllowedToModify && (
                        <ToolbarButton
                          icon={
                            <Box position="relative">
                              <BiCamera />
                              <>
                                <Icon
                                  as={BsGearFill}
                                  position="absolute"
                                  top="-2px"
                                  right="-2px"
                                  width="75%"
                                  height="75%"
                                  color="black"
                                />
                                <Icon
                                  as={BsGearFill}
                                  position="absolute"
                                  top="-2px"
                                  right="-2px"
                                  width="65%"
                                  height="65%"
                                  color="white"
                                />
                              </>
                            </Box>
                          }
                          label="正面を設定"
                          toolType={BlueprintEditorTools.BlueprintManager}
                          buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                          selectedTool={selectedTool}
                          changeTool={(newTool) =>
                            dispatch(setSelectedTool(newTool as unknown as BlueprintEditorTools))
                          }
                          toolTipPlacement="top"
                        />
                      )} */}
                        </HStack>
                      </Popover.Body>
                    </Popover.Content>
                  </Popover.Positioner>
                </Portal>
              </Popover.Root>
            </Box>
          )}
        </Box>
      ) : null}
    </HStack>
  )
}
